import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import Card from '../base/Card';
import Button from '../base/Button';
import './Settings.css';

const DEFAULT_BLOCKED_SITES = [
  'facebook.com',
  'instagram.com',
  'twitter.com',
  'x.com',
  'tiktok.com',
  'youtube.com',
  'reddit.com',
  'linkedin.com',
  'pinterest.com',
  'snapchat.com'
];

function Settings() {
  const { settings, updateSetting } = useAppContext();
  const [blockerEnabled, setBlockerEnabled] = useState(settings.notificationBlocker?.enabled || false);
  const [blockedSites, setBlockedSites] = useState(settings.notificationBlocker?.sites || DEFAULT_BLOCKED_SITES);
  const [newSite, setNewSite] = useState('');
  const [showExtensionInfo, setShowExtensionInfo] = useState(false);

  const handleToggleBlocker = () => {
    const newState = !blockerEnabled;
    setBlockerEnabled(newState);
    updateSetting('notificationBlocker', {
      enabled: newState,
      sites: blockedSites
    });
  };

  const handleAddSite = (e) => {
    e.preventDefault();
    if (newSite.trim() && !blockedSites.includes(newSite.trim().toLowerCase())) {
      const updatedSites = [...blockedSites, newSite.trim().toLowerCase()];
      setBlockedSites(updatedSites);
      updateSetting('notificationBlocker', {
        enabled: blockerEnabled,
        sites: updatedSites
      });
      setNewSite('');
    }
  };

  const handleRemoveSite = (site) => {
    const updatedSites = blockedSites.filter(s => s !== site);
    setBlockedSites(updatedSites);
    updateSetting('notificationBlocker', {
      enabled: blockerEnabled,
      sites: updatedSites
    });
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1 className="title-1">Settings</h1>
        <p className="description body-text">
          Customize your focus experience and manage your distractions.
        </p>
      </div>

      <div className="settings-sections">
        {/* Notification Blocker Section */}
        <Card className="settings-section-card">
          <div className="section-header">
            <div className="section-icon">🚫</div>
            <div className="section-title-group">
              <h2 className="section-title title-2">Notification blocker</h2>
              <p className="section-description body-text">
                Block all social media and distracting sites in one click on your laptop so you can stay focused instead of doom‑scrolling.
              </p>
            </div>
          </div>

          <div className="blocker-controls">
            <div className="toggle-group">
              <label className="toggle-label">
                <span className="toggle-text body-text">Enable blocker</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={blockerEnabled}
                    onChange={handleToggleBlocker}
                    className="toggle-input"
                  />
                  <span className={`toggle-slider ${blockerEnabled ? 'active' : ''}`} />
                </div>
              </label>
              {blockerEnabled && (
                <p className="toggle-status caption active-status">
                  ✓ Blocker is active
                </p>
              )}
              {!blockerEnabled && (
                <p className="toggle-status caption">
                  Blocker is disabled
                </p>
              )}
            </div>

            {blockerEnabled && (
              <div className="blocker-info-card">
                <div className="info-header">
                  <h3 className="info-title title-3">How to block sites</h3>
                  <button
                    className="info-toggle"
                    onClick={() => setShowExtensionInfo(!showExtensionInfo)}
                  >
                    {showExtensionInfo ? '−' : '+'}
                  </button>
                </div>
                
                {showExtensionInfo && (
                  <div className="extension-instructions">
                    <p className="instruction-text body-text">
                      To fully block sites, you'll need a browser extension. We recommend:
                    </p>
                    <ul className="extension-list">
                      <li>
                        <strong>Chrome/Edge:</strong>{' '}
                        <a 
                          href="https://chrome.google.com/webstore/detail/block-site/eiimnmioipafcokbfikbljfdeojpcgbh" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="extension-link"
                        >
                          Block Site Extension
                        </a>
                      </li>
                      <li>
                        <strong>Firefox:</strong>{' '}
                        <a 
                          href="https://addons.mozilla.org/en-US/firefox/addon/block-site/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="extension-link"
                        >
                          Block Site Add-on
                        </a>
                      </li>
                      <li>
                        <strong>Alternative:</strong> Use your browser's built-in site blocking 
                        or modify your hosts file (advanced users).
                      </li>
                    </ul>
                    <p className="instruction-note caption">
                      This app tracks your blocked sites list. Install the extension and 
                      add these sites to it for full protection.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="blocked-sites-section">
              <h3 className="sites-title title-3">Blocked Sites</h3>
              <p className="sites-description caption">
                Add sites you want to block. Copy these into your browser extension.
              </p>

              <div className="sites-list">
                {blockedSites.map(site => (
                  <div key={site} className="site-tag">
                    <span className="site-name">{site}</span>
                    <button
                      className="site-remove"
                      onClick={() => handleRemoveSite(site)}
                      aria-label={`Remove ${site}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <form className="add-site-form" onSubmit={handleAddSite}>
                <input
                  type="text"
                  className="site-input"
                  placeholder="example.com"
                  value={newSite}
                  onChange={(e) => setNewSite(e.target.value)}
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!newSite.trim() || blockedSites.includes(newSite.trim().toLowerCase())}
                >
                  Add Site
                </Button>
              </form>

              <Button
                variant="outline"
                className="copy-sites-button"
                onClick={() => {
                  navigator.clipboard.writeText(blockedSites.join('\n'));
                  alert('Sites copied to clipboard! Paste them into your browser extension.');
                }}
              >
                Copy All Sites
              </Button>
            </div>
          </div>
        </Card>

        {/* Timer Settings Section */}
        <Card className="settings-section-card">
          <div className="section-header">
            <div className="section-icon">⏱️</div>
            <div className="section-title-group">
              <h2 className="section-title title-2">Timer Preferences</h2>
              <p className="section-description body-text">
                Customize your focus and break durations.
              </p>
            </div>
          </div>

          <div className="timer-settings">
            <div className="setting-item">
              <label className="setting-label body-text">Focus Duration (minutes)</label>
              <input
                type="number"
                className="setting-input"
                value={settings.timerDuration || 25}
                onChange={(e) => updateSetting('timerDuration', parseInt(e.target.value) || 25)}
                min="1"
                max="120"
              />
            </div>

            <div className="setting-item">
              <label className="setting-label body-text">Short Break (minutes)</label>
              <input
                type="number"
                className="setting-input"
                value={settings.shortBreak || 5}
                onChange={(e) => updateSetting('shortBreak', parseInt(e.target.value) || 5)}
                min="1"
                max="30"
              />
            </div>

            <div className="setting-item">
              <label className="setting-label body-text">Long Break (minutes)</label>
              <input
                type="number"
                className="setting-input"
                value={settings.longBreak || 15}
                onChange={(e) => updateSetting('longBreak', parseInt(e.target.value) || 15)}
                min="1"
                max="60"
              />
            </div>
          </div>
        </Card>

        {/* Notification Settings Section */}
        <Card className="settings-section-card">
          <div className="section-header">
            <div className="section-icon">🔔</div>
            <div className="section-title-group">
              <h2 className="section-title title-2">Notifications</h2>
              <p className="section-description body-text">
                Control when and how you receive notifications.
              </p>
            </div>
          </div>

          <div className="notification-settings">
            <div className="toggle-group">
              <label className="toggle-label">
                <span className="toggle-text body-text">Enable notifications</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications || false}
                    onChange={(e) => updateSetting('notifications', e.target.checked)}
                    className="toggle-input"
                  />
                  <span className={`toggle-slider ${settings.notifications ? 'active' : ''}`} />
                </div>
              </label>
            </div>

            <div className="toggle-group">
              <label className="toggle-label">
                <span className="toggle-text body-text">Sound alerts</span>
                <div className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.soundEnabled || false}
                    onChange={(e) => updateSetting('soundEnabled', e.target.checked)}
                    className="toggle-input"
                  />
                  <span className={`toggle-slider ${settings.soundEnabled ? 'active' : ''}`} />
                </div>
              </label>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Settings;

